import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map, timeout } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { environment } from '../environments/environment';
import { MainPageComponent } from './pages/main-page/main-page.component';
import { TodoListPageComponent } from './pages/todo-list-page/todo-list-page.component';
import { AboutPageComponent } from './pages/about-page/about-page.component';
import { TodoItem, TodoListBlock, TodoSection, TodoSortingNode } from './todo.models';

interface TodoApiItem extends Partial<TodoItem> {
  id: number;
  type?: 'item';
  title?: string;
  name?: string;
  description?: string;
  completed?: boolean;
  done?: boolean;
  todo_list_id?: number;
  list_id?: number;
  todo_id?: number;
  created_at: string;
  updated_at: string;
}

interface TodoSectionApiItem {
  type?: 'section';
  id: number;
  todo_list_id?: number;
  list_id?: number;
  todo_id?: number;
  title?: string;
  created_at: string;
  updated_at: string;
  items?: TodoApiItem[];
  list?: TodoApiItem[];
}

type TodoApiBlock = TodoApiItem | TodoSectionApiItem;

interface TodoListApiItem {
  id: number;
  name?: string;
  title?: string;
  share_token?: string;
  shareToken?: string;
  creator_name?: string;
  creatorName?: string;
  owner_name?: string;
  ownerName?: string;
  role?: string;
  my_role?: string;
  access_level?: string;
  is_owner?: boolean;
  can_write?: boolean;
  pending_requests_count?: number;
  created_at?: string;
  updated_at?: string;
}

type ShareRole = 'reader' | 'writer' | 'owner';
type MembershipStatus = 'none' | 'pending' | 'member' | 'owner';

interface TodoListItem {
  id: number;
  name: string;
  shareToken: string;
  creatorName: string;
  currentUserRole: ShareRole;
  pendingRequests: number;
}

interface ShareMemberApiItem {
  id?: number;
  user_id?: number;
  userId?: number;
  name?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  role?: string;
  access_level?: string;
  is_owner?: boolean;
  is_creator?: boolean;
  is_self?: boolean;
  notifications_enabled?: boolean;
  notification_enabled?: boolean;
  notify_changes?: boolean;
  notifications?: boolean;
}

interface ShareMember {
  id: number;
  userName: string;
  role: ShareRole;
  isSelf: boolean;
  isCreator: boolean;
  notificationsEnabled: boolean;
}

interface NotificationPreferenceResponse {
  notifications_enabled?: boolean;
  notification_enabled?: boolean;
  notify_changes?: boolean;
  notifications?: boolean;
  data?: {
    notifications_enabled?: boolean;
    notification_enabled?: boolean;
    notify_changes?: boolean;
    notifications?: boolean;
  };
}

interface PermissionRequestApiItem {
  id?: number;
  request_id?: number;
  user_id?: number;
  userId?: number;
  user_name?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  created_at?: string;
  requested_at?: string;
}

interface PermissionRequest {
  id: number;
  userName: string;
  requestedAt: string;
}

interface ShareLinkCreateResponse {
  token?: string;
  code?: string;
  share_token?: string;
  link?: string;
  url?: string;
  share_url?: string;
}

interface ShareLinkPreviewResponse {
  token?: string;
  list_name?: string;
  todo_list_name?: string;
  name?: string;
  creator_name?: string;
  owner_name?: string;
  created_by?: string;
  membership_status?: string;
  status?: string;
}

interface ShareLinkPreview {
  token: string;
  listName: string;
  creatorName: string;
  membershipStatus: MembershipStatus;
}

interface BuildVersionResponse {
  version?: string;
}

interface TelegramInitDataEntry {
  key: string;
  value: string;
}

interface TelegramVerifiedUser {
  id: number;
  telegram_id: string;
  first_name: string;
  last_name: string;
  username: string;
  language_code: string;
  allows_write_to_pm: boolean;
  photo_url: string;
  created_at: string;
  updated_at: string;
}

interface TelegramVerifyResponse {
  ok: boolean;
  user?: TelegramVerifiedUser;
  auth_date?: number;
  access_token?: string;
  token_type?: string;
  expires_in?: number;
}

interface StoredAuthSession {
  accessToken: string;
  tokenType: string;
  expiresAt?: number;
  user?: TelegramVerifiedUser;
}

interface TelegramInitDataUser {
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

type TelegramVerifyState = 'idle' | 'verifying' | 'verified' | 'failed';
type TelegramColorScheme = 'light' | 'dark';
type TelegramEventName = 'themeChanged' | 'viewportChanged';

interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: Record<string, unknown>;
  platform: string;
  version: string;
  colorScheme: TelegramColorScheme;
  themeParams: TelegramThemeParams;
  viewportStableHeight?: number;
  viewportHeight?: number;
  ready(): void;
  expand(): void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: 'bg_color' | 'secondary_bg_color' | string) => void;
  setBackgroundColor?: (color: string) => void;
  openTelegramLink?: (url: string) => void;
  onEvent(eventName: TelegramEventName, callback: () => void): void;
  offEvent(eventName: TelegramEventName, callback: () => void): void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MainPageComponent,
    TodoListPageComponent,
    AboutPageComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly AUTH_STORAGE_KEY = 'keeperoon_auth_session';
  private static readonly ACTIVE_LIST_STORAGE_KEY = 'keeperoon_active_list_id';
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly onPopState = (): void => {
    this.refreshLocationDebugData();
    this.syncRouteFromUrl(`${window.location.pathname}${window.location.search}`);
    this.cdr.markForCheck();
  };

  private telegramWebApp?: TelegramWebApp;
  private readonly onThemeChanged = (): void => {
    this.syncThemeFromTelegram();
    this.cdr.markForCheck();
  };
  private readonly onViewportChanged = (): void => {
    this.syncViewportFromTelegram();
    this.cdr.markForCheck();
  };

  newTodo = '';
  isCreateSectionMode = false;
  newListName = '';
  renameListName = '';
  isSidebarOpen = false;
  isShareManagementPageOpen = false;
  currentPage: 'main' | 'list' | 'about' = 'main';
  isAddTodoPopupOpen = false;
  isAddListPopupOpen = false;
  isCreateTodoActionInFlight = false;
  isSectionActionInFlight = false;
  isCreateListActionInFlight = false;
  isEditListActionInFlight = false;
  isSaveSortingActionInFlight = false;
  private loadTodosRequestId = 0;
  todoBlocks: TodoListBlock[] = [];
  todoLists: TodoListItem[] = [];
  activeListId: number | null = null;
  isLoading = false;
  isStartupLoading = true;
  errorMessage = '';

  shareMembers: ShareMember[] = [];
  permissionRequests: PermissionRequest[] = [];
  shareLink = '';
  isLoadingShareData = false;
  isCreatingShareLink = false;
  isProcessingShareAction = false;

  activeShareToken = '';
  pendingShareToken = '';
  isSharePreviewLoading = false;
  sharePreview: ShareLinkPreview | null = null;
  isJoinActionInFlight = false;
  isNotificationPreferenceLoading = false;
  isNotificationPreferenceSaving = false;
  isActiveListNotificationsEnabled = false;

  isTelegramWebApp = false;
  hasTelegramInitData = false;
  telegramPlatform = '';
  telegramVersion = '';
  telegramStatusMessage = '';
  telegramValidationIssues: string[] = [];
  telegramVerifyState: TelegramVerifyState = 'idle';
  telegramVerifyMessage = 'Not started';
  telegramVerifyResponseDebug = '';
  telegramVerifyErrorDebug = '';
  telegramInitDataUnsafeDebug = '';
  telegramInitDataRaw = '';
  telegramInitDataEntries: TelegramInitDataEntry[] = [];
  debugLocationHref = '';
  debugLocationSearch = '';
  debugLocationHash = '';
  accessToken = '';
  accessTokenType = 'Bearer';
  buildVersion = 'unknown';
  backendVersion = 'unknown';
  profileName = 'User';
  profilePhotoUrl = '';
  profilePhotoClickCount = 0;
  isDebugInfoVisible = false;

  get remainingCount(): number {
    return this.activeListTodos.filter((todo) => !todo.completed).length;
  }

  get activeListName(): string {
    const activeList = this.todoLists.find((list) => list.id === this.activeListId);
    return activeList?.name || 'Today';
  }

  get toolbarTitle(): string {
    if (this.isAboutRoute) {
      return 'About';
    }

    if (this.isShareManagementPageOpen) {
      return 'List settings';
    }

    if (this.isSharePreviewPageOpen) {
      return 'Shared list';
    }

    const activeList = this.todoLists.find((list) => list.id === this.activeListId);
    return activeList?.name || 'Keeperoon';
  }

  get isMainRoute(): boolean {
    return this.currentPage === 'main';
  }

  get isListRoute(): boolean {
    return this.currentPage === 'list';
  }

  get isAboutRoute(): boolean {
    return this.currentPage === 'about';
  }

  get activeList(): TodoListItem | undefined {
    return this.todoLists.find((list) => list.id === this.activeListId);
  }

  get hasProfilePhoto(): boolean {
    return Boolean(this.profilePhotoUrl);
  }

  get activeListTodos(): TodoItem[] {
    return this.todoBlocks.flatMap((block) => (block.type === 'item' ? [block] : block.items));
  }

  get canWriteActiveList(): boolean {
    const role = this.activeList?.currentUserRole;
    return role === 'owner' || role === 'writer';
  }

  get isActiveListOwner(): boolean {
    return this.activeList?.currentUserRole === 'owner';
  }

  get isSharePreviewPageOpen(): boolean {
    return Boolean(this.activeShareToken);
  }

  get isActiveListShared(): boolean {
    return Boolean(this.activeList?.shareToken);
  }

  ngOnInit(): void {
    this.restoreActiveListId();
    this.refreshLocationDebugData();
    this.syncRouteFromUrl(`${window.location.pathname}${window.location.search}`);
    window.addEventListener('popstate', this.onPopState);
    this.initializeTelegramWebApp();
    this.loadBuildVersion();
    this.loadBackendVersion();
    this.openSharePreviewFromQuery();
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.onPopState);

    if (!this.telegramWebApp) {
      return;
    }

    this.telegramWebApp.offEvent('themeChanged', this.onThemeChanged);
    this.telegramWebApp.offEvent('viewportChanged', this.onViewportChanged);
  }

  onToolbarNavAction(): void {
    if (this.isAboutRoute || this.isListRoute) {
      this.openMainPage();
      return;
    }

    if (this.isShareManagementPageOpen) {
      this.closeShareManagementPage();
      return;
    }

    if (this.isSharePreviewPageOpen) {
      this.closeSharePreviewPage();
      return;
    }

    this.toggleSidebar();
  }

  openAddTodoPopup(): void {
    if (this.activeListId === null) {
      this.notifyError('Select or create a todo list first.');
      return;
    }

    if (!this.canWriteActiveList) {
      this.notifyError('You have read-only access in this shared list.');
      return;
    }

    this.isAddTodoPopupOpen = true;
  }

  closeAddTodoPopup(): void {
    this.isAddTodoPopupOpen = false;
    this.isCreateSectionMode = false;
    this.newTodo = '';
  }

  openAddListPopup(): void {
    this.isAddListPopupOpen = true;
  }

  closeAddListPopup(): void {
    this.isAddListPopupOpen = false;
    this.newListName = '';
    this.isCreateListActionInFlight = false;
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  closeSidebar(): void {
    this.isSidebarOpen = false;
  }

  onProfilePhotoClick(): void {
    if (this.isDebugInfoVisible) {
      return;
    }

    this.profilePhotoClickCount += 1;
    if (this.profilePhotoClickCount >= 10) {
      this.isDebugInfoVisible = true;
      this.notifyInfo('Debug info enabled.');
    }
  }

  selectList(listId: number): void {
    this.activeListId = listId;
    this.persistActiveListId(listId);
    this.isShareManagementPageOpen = false;
    this.closeSharePreviewPage();
    this.closeSidebar();
    this.navigateTo(`/lists/${listId}`);
    this.loadTodos();
    this.loadActiveListNotificationPreference();
  }

  openMainPage(): void {
    this.isShareManagementPageOpen = false;
    this.closeSharePreviewPage();
    this.closeSidebar();
    this.navigateTo('/lists');
  }

  openAboutPage(): void {
    this.isShareManagementPageOpen = false;
    this.closeSharePreviewPage();
    this.closeSidebar();
    this.navigateTo('/about');
  }

  closeAboutPage(): void {
    this.openMainPage();
  }

  openShareManagementPage(): void {
    if (!this.activeList) {
      return;
    }

    this.isShareManagementPageOpen = true;
    this.closeSharePreviewPage();
    this.closeSidebar();
    this.renameListName = this.activeList.name;
    this.shareLink = this.activeList?.shareToken ? this.buildShareUrl(this.activeList.shareToken) : '';
    this.loadActiveListNotificationPreference();

    if (this.isActiveListOwner) {
      this.loadShareManagementData();
      return;
    }

    this.shareMembers = [];
    this.permissionRequests = [];
    this.isLoadingShareData = false;
  }

  submitShareManagementListEdit(): void {
    if (this.isEditListActionInFlight || !this.activeList || !this.isActiveListOwner) {
      return;
    }

    this.renameTodoList(this.activeList);
  }

  removeActiveListFromShareManagement(): void {
    if (this.isEditListActionInFlight || !this.activeList || !this.isActiveListOwner) {
      return;
    }

    const isConfirmed = window.confirm(`Delete list "${this.activeList.name}"? This action cannot be undone.`);
    if (!isConfirmed) {
      return;
    }

    this.deleteTodoList(this.activeList);
  }

  closeShareManagementPage(): void {
    this.isShareManagementPageOpen = false;
  }

  loadTodos(): void {
    if (this.activeListId === null) {
      this.todoBlocks = [];
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    const requestId = ++this.loadTodosRequestId;

    this.http
      .get<TodoApiBlock[] | { items?: TodoApiBlock[]; data?: TodoApiBlock[]; result?: { items?: TodoApiBlock[]; data?: TodoApiBlock[] } }>(
        `${environment.apiBaseUrl}/todo/${this.activeListId}/items`,
        this.getAuthHttpOptions()
      )
      .pipe(
        timeout({ first: 10000 }),
        map((response) => this.mapTodoBlocksResponse(response)),
        catchError((error) => {
          if (requestId !== this.loadTodosRequestId) {
            return of([] as TodoListBlock[]);
          }

          this.notifyError(
            error?.name === 'TimeoutError'
              ? 'Loading todos timed out.'
              : 'Unable to load todos. Check backend host in environments files.'
          );
          return of([] as TodoListBlock[]);
        }),
        finalize(() => {
          if (requestId !== this.loadTodosRequestId) {
            return;
          }

          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((blocks) => {
        if (requestId !== this.loadTodosRequestId) {
          return;
        }

        this.todoBlocks = blocks;
        if (this.activeListId) {
          this.loadSectionsFallback(this.activeListId, requestId);
        }
      });
  }

  loadTodoLists(): void {
    this.errorMessage = '';

    this.http.get<TodoListApiItem[]>(`${environment.apiBaseUrl}/todo`, this.getAuthHttpOptions()).subscribe({
      next: (lists) => {
        this.todoLists = lists.map((list) => ({
          id: list.id,
          name: list.name || list.title || `List #${list.id}`,
          shareToken: list.share_token || list.shareToken || '',
          creatorName: list.creator_name || list.creatorName || list.owner_name || list.ownerName || this.profileName,
          currentUserRole: this.normalizeShareRole(
            list.my_role ||
              list.role ||
              list.access_level ||
              (list.is_owner ? 'owner' : list.can_write ? 'writer' : 'reader')
          ),
          pendingRequests: list.pending_requests_count || 0
        }));

        if (!this.todoLists.length) {
          this.activeListId = null;
          this.clearStoredActiveListId();
          this.todoBlocks = [];
          this.isActiveListNotificationsEnabled = false;
          this.isNotificationPreferenceLoading = false;
          this.tryOpenPendingSharePreview();
          this.finishStartupLoading();
          this.cdr.markForCheck();
          return;
        }

        if (!this.todoLists.some((list) => list.id === this.activeListId)) {
          this.activeListId = this.todoLists[0].id;
          this.persistActiveListId(this.activeListId);
          if (this.isListRoute) {
            this.navigateTo(`/lists/${this.activeListId}`, true);
          }
        }

        this.loadTodos();
        this.loadActiveListNotificationPreference();

        if (this.isShareManagementPageOpen && this.activeListId) {
          this.loadShareManagementData();
        }

        this.tryOpenPendingSharePreview();
        this.finishStartupLoading();
        this.cdr.markForCheck();
      },
      error: () => {
        this.notifyError('Unable to load todo lists.');
        this.isNotificationPreferenceLoading = false;
        this.tryOpenPendingSharePreview();
        this.finishStartupLoading();
        this.cdr.markForCheck();
      }
    });
  }

  createTodoList(): void {
    if (this.isCreateListActionInFlight) {
      return;
    }

    this.isCreateListActionInFlight = true;
    const name = this.newListName.trim();
    if (!name) {
      this.isCreateListActionInFlight = false;
      return;
    }

    this.errorMessage = '';

    this.http
      .post<TodoListApiItem>(`${environment.apiBaseUrl}/todo`, { name }, this.getAuthHttpOptions())
      .subscribe({
        next: (created) => {
          const newList: TodoListItem = {
            id: created.id,
            name: created.name || created.title || name,
            shareToken: '',
            creatorName: this.profileName,
            currentUserRole: 'owner',
            pendingRequests: 0
          };
          this.todoLists = [newList, ...this.todoLists];
          this.activeListId = newList.id;
          this.persistActiveListId(this.activeListId);
          this.newListName = '';
          this.closeAddListPopup();
          this.loadTodos();
          this.isCreateListActionInFlight = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notifyError('Unable to create todo list.');
          this.isCreateListActionInFlight = false;
          this.cdr.markForCheck();
        }
      });
  }

  renameTodoList(list: TodoListItem): void {
    if (!this.isActiveListOwner) {
      this.notifyError('Only owner can rename this list.');
      return;
    }

    this.isEditListActionInFlight = true;
    const name = this.renameListName.trim();
    if (!name || name === list.name) {
      this.isEditListActionInFlight = false;
      this.renameListName = list.name;
      this.cdr.markForCheck();
      return;
    }

    this.errorMessage = '';

    this.http
      .patch<TodoListApiItem>(`${environment.apiBaseUrl}/todo/${list.id}`, { name }, this.getAuthHttpOptions())
      .subscribe({
        next: (updated) => {
          const previousName = list.name;
          const nextName = updated.name || updated.title || name;
          this.todoLists = this.todoLists.map((item) =>
            item.id === updated.id
              ? {
                  ...item,
                  name: nextName
                }
              : item
          );
          this.renameListName = nextName;
          this.notifySharedListChange(updated.id, 'list_renamed', `List renamed from "${previousName}" to "${nextName}".`);
          this.isEditListActionInFlight = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notifyError('Unable to rename todo list.');
          this.isEditListActionInFlight = false;
          this.cdr.markForCheck();
        }
      });
  }

  deleteTodoList(list: TodoListItem): void {
    if (!this.isActiveListOwner) {
      this.notifyError('Only owner can delete this list.');
      return;
    }

    this.isEditListActionInFlight = true;
    this.errorMessage = '';
    const wasActiveList = this.activeListId === list.id;

    this.http.delete<void>(`${environment.apiBaseUrl}/todo/${list.id}`, this.getAuthHttpOptions()).subscribe({
      next: () => {
        this.notifySharedListChange(list.id, 'list_deleted', `List "${list.name}" was deleted.`);
        this.todoLists = this.todoLists.filter((item) => item.id !== list.id);
        this.todoBlocks = [];

        if (wasActiveList) {
          this.activeListId = this.todoLists.length ? this.todoLists[0].id : null;
          if (this.activeListId === null) {
            this.clearStoredActiveListId();
          } else {
            this.persistActiveListId(this.activeListId);
          }
        }

        if (wasActiveList) {
          this.isShareManagementPageOpen = false;
        }

        this.isEditListActionInFlight = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notifyError('Unable to delete todo list.');
        this.isEditListActionInFlight = false;
        this.cdr.markForCheck();
      }
    });
  }

  addTodo(): void {
    if (this.isCreateTodoActionInFlight) {
      return;
    }

    const title = this.newTodo.trim();
    if (!title) {
      return;
    }

    this.errorMessage = '';

    if (this.activeListId === null) {
      this.notifyError('Select or create a todo list first.');
      return;
    }

    if (!this.canWriteActiveList) {
      this.notifyError('You have read-only access in this shared list.');
      return;
    }

    this.isCreateTodoActionInFlight = true;

    if (this.isCreateSectionMode) {
      this.http
        .post<unknown>(`${environment.apiBaseUrl}/todo/${this.activeListId}/sections`, { title }, this.getAuthHttpOptions())
        .pipe(
          finalize(() => {
            this.isCreateTodoActionInFlight = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: () => {
            this.closeAddTodoPopup();
            this.notifySuccess('Section created.');
            this.loadTodos();
          },
          error: (error) => {
            this.notifyError(this.mapApiErrorToMessage(error, 'Unable to create section.'));
          }
        });
      return;
    }

    this.http
      .post<TodoApiItem | { item?: TodoApiItem; data?: TodoApiItem; todo?: TodoApiItem }>(
        `${environment.apiBaseUrl}/todo/${this.activeListId}/items`,
        {
          title,
          description: '',
          completed: false
        },
        this.getAuthHttpOptions()
      )
      .pipe(
        finalize(() => {
          this.isCreateTodoActionInFlight = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response) => {
          this.closeAddTodoPopup();
          this.newTodo = '';

          const created = 'id' in response ? response : response.item || response.data || response.todo;

          if (created?.id) {
            const createdTitle = created.title || created.name || title;
            this.notifySharedListChange(this.activeListId, 'todo_created', `Task added: "${createdTitle}".`);
            this.loadTodos();
            return;
          }

          this.loadTodos();
          this.notifySharedListChange(this.activeListId, 'todo_created', `Task added: "${title}".`);
        },
        error: () => {
          this.notifyError('Unable to create todo.');
        }
      });
  }

  createSection(title: string): void {
    if (!this.activeListId || !this.canWriteActiveList || this.isSectionActionInFlight) {
      return;
    }

    this.isSectionActionInFlight = true;
    this.http
      .post<unknown>(`${environment.apiBaseUrl}/todo/${this.activeListId}/sections`, { title }, this.getAuthHttpOptions())
      .pipe(
        finalize(() => {
          this.isSectionActionInFlight = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.notifySuccess('Section created.');
          this.loadTodos();
        },
        error: (error) => {
          this.notifyError(this.mapApiErrorToMessage(error, 'Unable to create section.'));
        }
      });
  }

  renameSection(sectionId: number, title: string): void {
    if (!this.activeListId || !this.canWriteActiveList || this.isSectionActionInFlight) {
      return;
    }

    this.isSectionActionInFlight = true;
    this.http
      .patch<unknown>(`${environment.apiBaseUrl}/todo/${this.activeListId}/sections/${sectionId}`, { title }, this.getAuthHttpOptions())
      .pipe(
        finalize(() => {
          this.isSectionActionInFlight = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.notifySuccess('Section renamed.');
          this.loadTodos();
        },
        error: (error) => {
          this.notifyError(this.mapApiErrorToMessage(error, 'Unable to rename section.'));
        }
      });
  }

  deleteSection(sectionId: number): void {
    if (!this.activeListId || !this.canWriteActiveList || this.isSectionActionInFlight) {
      return;
    }

    this.isSectionActionInFlight = true;
    this.http
      .delete<void>(`${environment.apiBaseUrl}/todo/${this.activeListId}/sections/${sectionId}`, this.getAuthHttpOptions())
      .pipe(
        finalize(() => {
          this.isSectionActionInFlight = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.notifySuccess('Section deleted.');
          this.loadTodos();
        },
        error: (error) => {
          this.notifyError(this.mapApiErrorToMessage(error, 'Unable to delete section.'));
        }
      });
  }

  toggleTodo(todo: TodoItem): void {
    if (!this.canWriteActiveList) {
      this.notifyError('You have read-only access in this shared list.');
      return;
    }

    const newDoneValue = !todo.completed;
    this.errorMessage = '';

    this.http
      .patch<TodoApiItem>(
        `${environment.apiBaseUrl}/todo/${todo.todo_list_id}/items/${todo.id}`,
        { completed: newDoneValue },
        this.getAuthHttpOptions()
      )
      .subscribe({
        next: (updated) => {
          const changedTitle = updated.title || updated.name || todo.title;
          const changedDone = updated.completed ?? updated.done ?? newDoneValue;
          this.todoBlocks = this.todoBlocks.map((block) => this.patchTodoItemInBlock(block, updated.id, changedTitle, changedDone));
          this.notifySharedListChange(
            todo.todo_list_id,
            'todo_status_changed',
            `Task "${changedTitle}" marked as ${changedDone ? 'completed' : 'active'}.`
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.notifyError('Unable to update todo.');
          this.cdr.markForCheck();
        }
      });
  }

  removeTodo(todo: TodoItem): void {
    if (!this.canWriteActiveList) {
      this.notifyError('You have read-only access in this shared list.');
      return;
    }

    this.errorMessage = '';

    const isConfirmed = window.confirm(`Delete task "${todo.title}"? This action cannot be undone.`);
    if (!isConfirmed) {
      return;
    }

    this.http.delete<void>(`${environment.apiBaseUrl}/todo/${todo.todo_list_id}/items/${todo.id}`, this.getAuthHttpOptions()).subscribe({
      next: () => {
        this.notifySharedListChange(todo.todo_list_id, 'todo_deleted', `Task removed: "${todo.title}".`);
        this.loadTodos();
        this.cdr.markForCheck();
      },
      error: () => {
        this.notifyError('Unable to delete todo.');
        this.cdr.markForCheck();
      }
    });
  }

  renameTodo(todo: TodoItem, title: string): void {
    if (!this.canWriteActiveList) {
      this.notifyError('You have read-only access in this shared list.');
      return;
    }

    const nextTitle = title.trim();
    if (!nextTitle || nextTitle === todo.title) {
      return;
    }

    this.http
      .patch<TodoApiItem>(
        `${environment.apiBaseUrl}/todo/${todo.todo_list_id}/items/${todo.id}`,
        { title: nextTitle },
        this.getAuthHttpOptions()
      )
      .subscribe({
        next: (updated) => {
          const changedTitle = updated.title || updated.name || nextTitle;
          const changedDone = updated.completed ?? updated.done ?? todo.completed;
          this.todoBlocks = this.todoBlocks.map((block) => this.patchTodoItemInBlock(block, todo.id, changedTitle, changedDone));
          this.notifySuccess('Task renamed.');
          this.notifySharedListChange(todo.todo_list_id, 'todo_renamed', `Task renamed to "${changedTitle}".`);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.notifyError(this.mapApiErrorToMessage(error, 'Unable to rename task.'));
          this.cdr.markForCheck();
        }
      });
  }

  onBlocksReordered(blocks: TodoListBlock[]): void {
    if (!this.canWriteActiveList || !this.activeListId) {
      return;
    }

    const previousBlocks = this.cloneTodoBlocks(this.todoBlocks);
    this.todoBlocks = blocks;

    const payload = this.buildSortingPayload(blocks);
    this.isSaveSortingActionInFlight = true;
    this.http
      .put<{ ok?: boolean }>(`${environment.apiBaseUrl}/todo/${this.activeListId}/sorting`, payload, this.getAuthHttpOptions())
      .pipe(
        finalize(() => {
          this.isSaveSortingActionInFlight = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.todoBlocks = previousBlocks;
          this.notifyError(this.buildSortingErrorMessage(error));
          this.cdr.markForCheck();
        }
      });
  }

  createShareLink(): void {
    if (!this.activeListId || !this.isActiveListOwner || this.isCreatingShareLink) {
      return;
    }

    if (this.activeList?.shareToken) {
      this.notifyInfo('Shared link already exists for this list.');
      this.shareLink = this.buildShareUrl(this.activeList.shareToken);
      return;
    }

    this.isCreatingShareLink = true;
    this.tryCreateShareLink([`/todo/${this.activeListId}/share-links`, `/todo/${this.activeListId}/shares/links`]);
  }

  shareByNativeDialog(): void {
    if (!this.shareLink) {
      return;
    }

    if (this.isTelegramWebApp) {
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(this.shareLink)}&text=${encodeURIComponent(
        `Join my shared todo list: ${this.activeListName}`
      )}`;

      if (this.telegramWebApp?.openTelegramLink) {
        this.telegramWebApp.openTelegramLink(shareUrl);
      } else {
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    const payload = {
      title: `${this.activeListName} - shared list`,
      text: `Join my shared todo list: ${this.activeListName}`,
      url: this.shareLink
    };

    if (navigator.share) {
      navigator.share(payload).catch(() => {
        this.copyShareLink();
      });
      return;
    }

    this.copyShareLink();
  }

  copyShareLink(): void {
    if (!this.shareLink) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(this.shareLink)
        .then(() => {
          this.showSnackBar('Share link copied', 'success');
          this.cdr.markForCheck();
        })
        .catch(() => {
          this.showSnackBar('Unable to copy link', 'error');
          this.cdr.markForCheck();
        });
      return;
    }

    this.showSnackBar('Copy is not available', 'info');
  }

  private showSnackBar(message: string, variant: 'success' | 'info' | 'error' = 'info'): void {
    this.snackBar.open(message, undefined, {
      duration: 2800,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: [`keeperoon-snackbar`, `keeperoon-snackbar-${variant}`]
    });
  }

  private notifySuccess(message: string): void {
    this.showSnackBar(message, 'success');
  }

  private notifyInfo(message: string): void {
    this.showSnackBar(message, 'info');
  }

  private notifyError(message: string): void {
    this.showSnackBar(message, 'error');
  }

  loadShareManagementData(): void {
    if (!this.activeListId || !this.isActiveListOwner) {
      return;
    }

    this.isLoadingShareData = true;
    const members$ = this.http
      .get<ShareMemberApiItem[] | { items?: ShareMemberApiItem[]; data?: ShareMemberApiItem[] }>(
        `${environment.apiBaseUrl}/todo/${this.activeListId}/shares/members`,
        this.getAuthHttpOptions()
      )
      .pipe(catchError(() => of([] as ShareMemberApiItem[])));

    const requests$ = this.http
      .get<PermissionRequestApiItem[] | { items?: PermissionRequestApiItem[]; data?: PermissionRequestApiItem[] }>(
        `${environment.apiBaseUrl}/todo/${this.activeListId}/shares/requests`,
        this.getAuthHttpOptions()
      )
      .pipe(catchError(() => of([] as PermissionRequestApiItem[])));

    forkJoin([members$, requests$])
      .pipe(
        finalize(() => {
          this.isLoadingShareData = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(([membersResponse, requestsResponse]) => {
        const membersRaw = this.extractCollection(membersResponse);
        const requestsRaw = this.extractCollection(requestsResponse);

        this.shareMembers = membersRaw
          .map((item, index) => this.mapShareMember(item, index + 1))
          .filter((item): item is ShareMember => Boolean(item));

        this.permissionRequests = requestsRaw
          .map((item, index) => this.mapPermissionRequest(item, index + 1))
          .filter((item): item is PermissionRequest => Boolean(item));

        this.updateActiveListPendingCount(this.permissionRequests.length);
      });
  }

  changeMemberRole(member: ShareMember, role: ShareRole): void {
    if (!this.activeListId || this.isProcessingShareAction || member.role === role) {
      return;
    }

    this.isProcessingShareAction = true;
    this.http
      .patch<void>(
        `${environment.apiBaseUrl}/todo/${this.activeListId}/shares/members/${member.id}`,
        { role },
        this.getAuthHttpOptions()
      )
      .pipe(
        finalize(() => {
          this.isProcessingShareAction = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.shareMembers = this.shareMembers.map((item) =>
            item.id === member.id
              ? {
                  ...item,
                  role
                }
              : item
          );
          this.notifySuccess(`${member.userName} is now ${this.roleLabel(role)}.`);
          this.loadTodoLists();
        },
        error: (error) => {
          const backendMessage = error?.error?.message || error?.error?.error;
          if (error?.status === 409 || backendMessage === 'last_owner_forbidden') {
            this.notifyError('List must have at least one owner.');
            return;
          }

          this.notifyError(backendMessage || 'Unable to update access level.');
        }
      });
  }

  approvePermissionRequest(request: PermissionRequest): void {
    this.resolvePermissionRequest(request, 'approve');
  }

  rejectPermissionRequest(request: PermissionRequest): void {
    this.resolvePermissionRequest(request, 'reject');
  }

  openSharePreviewByToken(token: string): void {
    if (!token) {
      return;
    }

    this.activeShareToken = token;
    this.isShareManagementPageOpen = false;
    this.closeSidebar();

    this.isSharePreviewLoading = true;
    this.sharePreview = null;

    this.http
      .get<ShareLinkPreviewResponse>(`${environment.apiBaseUrl}/todo/share-links/${token}`, this.getAuthHttpOptions())
      .pipe(
        catchError(() =>
          this.http.get<ShareLinkPreviewResponse>(`${environment.apiBaseUrl}/todo/shares/${token}`, this.getAuthHttpOptions())
        ),
        finalize(() => {
          this.isSharePreviewLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (response) => {
          const status = this.normalizeMembershipStatus(response.membership_status || response.status);
          this.sharePreview = {
            token,
            listName: response.list_name || response.todo_list_name || response.name || 'Shared todo list',
            creatorName: response.creator_name || response.owner_name || response.created_by || 'Unknown',
            membershipStatus: status
          };
        },
        error: () => {
          this.notifyError('Unable to open shared link. It may be expired or invalid.');
        }
      });
  }

  closeSharePreviewPage(): void {
    this.activeShareToken = '';
    this.sharePreview = null;
  }

  requestToJoinSharedList(): void {
    if (!this.activeShareToken || this.isJoinActionInFlight) {
      return;
    }

    this.isJoinActionInFlight = true;
    this.http
      .post<void>(`${environment.apiBaseUrl}/todo/share-links/${this.activeShareToken}/join`, {}, this.getAuthHttpOptions())
      .pipe(
        catchError(() =>
          this.http.post<void>(`${environment.apiBaseUrl}/todo/shares/${this.activeShareToken}/join`, {}, this.getAuthHttpOptions())
        ),
        finalize(() => {
          this.isJoinActionInFlight = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.notifySuccess('Join request sent. Owner will review your request.');
          if (this.sharePreview) {
            this.sharePreview = {
              ...this.sharePreview,
              membershipStatus: 'pending'
            };
          }
          this.loadTodoLists();
        },
        error: () => {
          this.notifyError('Unable to send join request.');
        }
      });
  }

  roleLabel(role: ShareRole): string {
    if (role === 'owner') {
      return 'Owner';
    }

    if (role === 'writer') {
      return 'Writer';
    }

    return 'Read only';
  }

  canJoinSharedList(): boolean {
    return Boolean(this.sharePreview && this.sharePreview.membershipStatus === 'none');
  }

  toggleActiveListNotifications(): void {
    if (!this.activeListId || !this.isActiveListShared || this.isNotificationPreferenceSaving) {
      return;
    }

    const nextValue = !this.isActiveListNotificationsEnabled;
    this.isNotificationPreferenceSaving = true;

    this.tryToggleNotificationPreference(
      [
        `/todo/${this.activeListId}/shares/notifications/me`,
        `/todo/${this.activeListId}/shares/me/notifications`,
        `/todo/${this.activeListId}/notifications/me`
      ],
      nextValue
    );
  }

  private loadActiveListNotificationPreference(): void {
    if (!this.activeListId || !this.isActiveListShared) {
      this.isNotificationPreferenceLoading = false;
      this.isActiveListNotificationsEnabled = false;
      return;
    }

    this.isNotificationPreferenceLoading = true;
    this.tryLoadNotificationPreference([
      `/todo/${this.activeListId}/shares/notifications/me`,
      `/todo/${this.activeListId}/shares/me/notifications`,
      `/todo/${this.activeListId}/notifications/me`
    ]);
  }

  private tryLoadNotificationPreference(endpoints: string[]): void {
    if (!endpoints.length) {
      this.isNotificationPreferenceLoading = false;
      this.isActiveListNotificationsEnabled = false;
      this.cdr.markForCheck();
      return;
    }

    const [currentEndpoint, ...nextEndpoints] = endpoints;

    this.http
      .get<NotificationPreferenceResponse>(`${environment.apiBaseUrl}${currentEndpoint}`, this.getAuthHttpOptions())
      .subscribe({
        next: (response) => {
          this.isActiveListNotificationsEnabled = this.extractNotificationPreference(response);
          this.isNotificationPreferenceLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (!nextEndpoints.length) {
            this.isActiveListNotificationsEnabled = false;
            this.isNotificationPreferenceLoading = false;
            this.cdr.markForCheck();
            return;
          }

          this.tryLoadNotificationPreference(nextEndpoints);
        }
      });
  }

  private tryToggleNotificationPreference(endpoints: string[], enabled: boolean): void {
    if (!endpoints.length) {
      this.isNotificationPreferenceSaving = false;
      this.notifyError('Unable to update notifications.');
      this.cdr.markForCheck();
      return;
    }

    const [currentEndpoint, ...nextEndpoints] = endpoints;
    this.http
      .patch<void>(
        `${environment.apiBaseUrl}${currentEndpoint}`,
        {
          notifications_enabled: enabled,
          notify_changes: enabled
        },
        this.getAuthHttpOptions()
      )
      .subscribe({
        next: () => {
          this.isActiveListNotificationsEnabled = enabled;
          this.isNotificationPreferenceSaving = false;
          this.notifySuccess(enabled ? 'Notifications enabled for this shared list.' : 'Notifications disabled for this shared list.');
          this.cdr.markForCheck();
        },
        error: () => {
          if (!nextEndpoints.length) {
            this.isNotificationPreferenceSaving = false;
            this.notifyError('Unable to update notifications.');
            this.cdr.markForCheck();
            return;
          }

          this.tryToggleNotificationPreference(nextEndpoints, enabled);
        }
      });
  }

  private extractNotificationPreference(response: NotificationPreferenceResponse | null | undefined): boolean {
    const payload = response?.data || response || {};
    const explicitValue =
      payload.notifications_enabled ??
      payload.notification_enabled ??
      payload.notify_changes ??
      payload.notifications;

    return explicitValue === true;
  }

  private notifySharedListChange(listId: number | null, action: string, details: string): void {
    if (!listId) {
      return;
    }

    const list = this.todoLists.find((item) => item.id === listId);
    if (!list?.shareToken) {
      return;
    }

    const payload = {
      action,
      details,
      list_id: listId,
      list_name: list.name,
      actor_name: this.profileName,
      source: 'telegram_miniapp',
      changed_at: new Date().toISOString(),
      meta: {
        telegram_platform: this.telegramPlatform || 'unknown',
        telegram_version: this.telegramVersion || 'unknown'
      }
    };

    this.trySendSharedListChangeEvent(
      [
        `/todo/${listId}/shares/notifications/events`,
        `/todo/${listId}/notifications/events`,
        `/todo/${listId}/shares/notifications`,
        `/todo/${listId}/notifications`
      ],
      payload
    );
  }

  private trySendSharedListChangeEvent(endpoints: string[], payload: Record<string, unknown>): void {
    if (!endpoints.length) {
      return;
    }

    const [currentEndpoint, ...nextEndpoints] = endpoints;
    this.http.post<void>(`${environment.apiBaseUrl}${currentEndpoint}`, payload, this.getAuthHttpOptions()).subscribe({
      next: () => {
        // Best effort notification dispatch; no UI action needed.
      },
      error: () => {
        this.trySendSharedListChangeEvent(nextEndpoints, payload);
      }
    });
  }

  private resolvePermissionRequest(request: PermissionRequest, action: 'approve' | 'reject'): void {
    if (!this.activeListId || this.isProcessingShareAction) {
      return;
    }

    this.isProcessingShareAction = true;
    this.http
      .post<void>(
        `${environment.apiBaseUrl}/todo/${this.activeListId}/shares/requests/${request.id}/${action}`,
        {},
        this.getAuthHttpOptions()
      )
      .pipe(
        finalize(() => {
          this.isProcessingShareAction = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.permissionRequests = this.permissionRequests.filter((item) => item.id !== request.id);
          this.updateActiveListPendingCount(this.permissionRequests.length);
          this.notifySuccess(
            action === 'approve'
              ? `${request.userName} can now access this list.`
              : `${request.userName}'s join request was rejected.`
          );
          this.loadTodoLists();
        },
        error: () => {
          this.notifyError(`Unable to ${action} join request.`);
        }
      });
  }

  private tryCreateShareLink(endpoints: string[]): void {
    if (!endpoints.length) {
      this.isCreatingShareLink = false;
      this.notifyError('Unable to create shared link.');
      this.cdr.markForCheck();
      return;
    }

    const [currentEndpoint, ...nextEndpoints] = endpoints;

    this.http
      .post<ShareLinkCreateResponse>(`${environment.apiBaseUrl}${currentEndpoint}`, {}, this.getAuthHttpOptions())
      .pipe(
        finalize(() => {
          if (!nextEndpoints.length || this.shareLink) {
            this.isCreatingShareLink = false;
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe({
        next: (response) => {
          const token = response.token || response.share_token || response.code || '';
          const apiLink = response.link || response.url || response.share_url || '';
          const runtimeLink = this.buildShareUrl(token);
          this.shareLink = this.isTelegramWebApp && runtimeLink ? runtimeLink : apiLink || runtimeLink;
          if (this.shareLink) {
            this.notifySuccess('Share link is ready.');
          }
          if (token && this.activeListId) {
            this.todoLists = this.todoLists.map((list) =>
              list.id === this.activeListId
                ? {
                    ...list,
                    shareToken: token
                  }
                : list
            );
            this.loadActiveListNotificationPreference();
          }

          if (!this.shareLink) {
            this.notifyError('Backend did not return share link.');
          }
        },
        error: () => {
          if (!nextEndpoints.length) {
            this.isCreatingShareLink = false;
            this.notifyError('Unable to create shared link.');
            this.cdr.markForCheck();
            return;
          }

          this.tryCreateShareLink(nextEndpoints);
        }
      });
  }

  private buildShareUrl(token: string): string {
    if (!token) {
      return '';
    }

    if (this.isTelegramWebApp) {
      const telegramLink = this.buildTelegramMiniAppLink(token);
      if (telegramLink) {
        return telegramLink;
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set('share', token);
    return url.toString();
  }

  private openSharePreviewFromQuery(): void {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const shareToken =
      query.get('share') ||
      query.get('startapp') ||
      query.get('start_param') ||
      query.get('tgWebAppStartParam') ||
      hash.get('share') ||
      hash.get('startapp') ||
      hash.get('start_param') ||
      hash.get('tgWebAppStartParam') ||
      this.getStartParamFromInitData(this.telegramWebApp?.initData || '');
    if (!shareToken) {
      return;
    }

    this.pendingShareToken = shareToken;
    this.tryOpenPendingSharePreview();
  }

  private tryOpenPendingSharePreview(): void {
    if (!this.pendingShareToken || this.isSharePreviewLoading || this.isSharePreviewPageOpen) {
      return;
    }

    const token = this.pendingShareToken;
    this.pendingShareToken = '';
    this.openSharePreviewByToken(token);
  }

  private buildTelegramMiniAppLink(token: string): string {
    const bot = (environment.telegramBotUsername || '').trim().replace(/^@/, '');
    if (!bot) {
      return '';
    }

    const shortName = (environment.telegramMiniAppShortName || '').trim().replace(/^\//, '');
    const encodedToken = encodeURIComponent(token);

    if (shortName) {
      return `https://t.me/${bot}/${shortName}?startapp=${encodedToken}`;
    }

    return `https://t.me/${bot}?startapp=${encodedToken}`;
  }

  private getStartParamFromInitData(initData: string): string {
    if (!initData) {
      return '';
    }

    const params = new URLSearchParams(initData);
    return params.get('start_param') || params.get('startapp') || '';
  }

  private updateActiveListPendingCount(value: number): void {
    if (!this.activeListId) {
      return;
    }

    this.todoLists = this.todoLists.map((list) =>
      list.id === this.activeListId
        ? {
            ...list,
            pendingRequests: value
          }
        : list
    );
  }

  private extractCollection<T>(
    response: T[] | { items?: T[]; data?: T[] } | null | undefined
  ): T[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    return response.items || response.data || [];
  }

  private mapTodoBlocksResponse(
    response: TodoApiBlock[] | { items?: TodoApiBlock[]; data?: TodoApiBlock[]; result?: { items?: TodoApiBlock[]; data?: TodoApiBlock[] } }
  ): TodoListBlock[] {
    const items = Array.isArray(response)
      ? response
      : response.items || response.data || response.result?.items || response.result?.data || [];

    return items
      .map((item) => {
        if (this.isApiSection(item)) {
          return this.mapApiSection(item);
        }

        return this.mapApiItem(item);
      })
      .filter((item): item is TodoListBlock => Boolean(item));
  }

  private mapApiItem(item: TodoApiItem): TodoItem | null {
    if (!item?.id) {
      return null;
    }

    const listId = item.todo_list_id ?? item.list_id ?? item.todo_id ?? this.activeListId ?? 0;
    return {
      type: 'item',
      id: item.id,
      todo_list_id: listId,
      title: item.title || item.name || `Item #${item.id}`,
      description: item.description || '',
      completed: item.completed ?? item.done ?? false,
      created_at: item.created_at || '',
      updated_at: item.updated_at || ''
    };
  }

  private mapApiSection(section: TodoSectionApiItem): TodoSection | null {
    if (!section?.id) {
      return null;
    }

    const listId = section.todo_list_id ?? section.list_id ?? section.todo_id ?? this.activeListId ?? 0;
    const mappedItems = (section.items || section.list || [])
      .map((item) => this.mapApiItem(item))
      .filter((item): item is TodoItem => Boolean(item));

    return {
      type: 'section',
      id: section.id,
      todo_list_id: listId,
      title: section.title || `Section #${section.id}`,
      created_at: section.created_at || '',
      updated_at: section.updated_at || '',
      items: mappedItems
    };
  }

  private isApiSection(item: TodoApiBlock): item is TodoSectionApiItem {
    return (
      (item as TodoSectionApiItem)?.type === 'section' ||
      Array.isArray((item as TodoSectionApiItem)?.items) ||
      Array.isArray((item as TodoSectionApiItem)?.list)
    );
  }

  private buildSortingPayload(blocks: TodoListBlock[]): TodoSortingNode[] {
    return blocks.map((block) => {
      if (block.type === 'item') {
        return block.id;
      }

      return {
        type: 'section',
        id: block.id,
        list: block.items.map((item) => item.id)
      };
    });
  }

  private mapApiErrorToMessage(error: unknown, fallback: string): string {
    const rawError = error as { error?: { message?: string; error?: string; details?: string }; status?: number };
    const backendMessage =
      rawError?.error?.message ||
      rawError?.error?.error ||
      rawError?.error?.details ||
      '';

    if (rawError?.status === 400 && backendMessage) {
      return `Invalid request: ${backendMessage}`;
    }

    if (rawError?.status === 403) {
      return 'You do not have permission for this action.';
    }

    if (rawError?.status === 404) {
      return 'List data is outdated. Refreshing...';
    }

    if (backendMessage) {
      return backendMessage;
    }

    return fallback;
  }

  private buildSortingErrorMessage(error: unknown): string {
    const message = this.mapApiErrorToMessage(error, 'Unable to save new order.');
    const lowered = message.toLowerCase();

    if (lowered.includes('duplicate')) {
      return 'Invalid sorting data: duplicate IDs found. Previous order was restored.';
    }

    if (lowered.includes('unknown') || lowered.includes('not found')) {
      return 'Invalid sorting data: some items/sections no longer exist. Previous order was restored.';
    }

    if (lowered.includes('schema') || lowered.includes('invalid json') || lowered.includes('invalid')) {
      return 'Invalid sorting data format. Previous order was restored.';
    }

    return `${message} Previous order was restored.`;
  }

  private loadSectionsFallback(listId: number, requestId: number): void {
    this.http
      .get<TodoSectionApiItem[] | { items?: TodoSectionApiItem[]; data?: TodoSectionApiItem[] }>(
        `${environment.apiBaseUrl}/todo/${listId}/sections`,
        this.getAuthHttpOptions()
      )
      .pipe(catchError(() => of([] as TodoSectionApiItem[])))
      .subscribe((response) => {
        if (requestId !== this.loadTodosRequestId || this.activeListId !== listId) {
          return;
        }

        const sections = this.extractCollection(response)
          .map((section) => this.mapApiSection(section))
          .filter((section): section is TodoSection => Boolean(section));

        if (!sections.length) {
          return;
        }

        const existingSectionIds = new Set(this.todoBlocks.filter((block) => block.type === 'section').map((section) => section.id));
        const missingSections = sections.filter((section) => !existingSectionIds.has(section.id));

        if (!missingSections.length) {
          return;
        }

        this.todoBlocks = [...this.todoBlocks, ...missingSections.map((section) => ({ ...section, items: [] }))];
        this.cdr.markForCheck();
      });
  }

  private cloneTodoBlocks(blocks: TodoListBlock[]): TodoListBlock[] {
    return blocks.map((block) => {
      if (block.type === 'item') {
        return { ...block };
      }

      return {
        ...block,
        items: block.items.map((item) => ({ ...item }))
      };
    });
  }

  private patchTodoItemInBlock(block: TodoListBlock, itemId: number, title: string, completed: boolean): TodoListBlock {
    if (block.type === 'item') {
      if (block.id !== itemId) {
        return block;
      }

      return {
        ...block,
        title,
        completed
      };
    }

    return {
      ...block,
      items: block.items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              title,
              completed
            }
          : item
      )
    };
  }

  private mapShareMember(item: ShareMemberApiItem, fallbackId: number): ShareMember | null {
    const id = item.id || item.user_id || item.userId || fallbackId;
    if (!id) {
      return null;
    }

    return {
      id,
      userName:
        item.full_name ||
        item.name ||
        `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
        item.username ||
        `User #${id}`,
      role: this.normalizeShareRole(item.role || item.access_level || (item.is_owner ? 'owner' : 'reader')),
      isSelf: Boolean(item.is_self),
      isCreator: Boolean(item.is_owner || item.is_creator),
      notificationsEnabled: Boolean(
        item.notifications_enabled ?? item.notification_enabled ?? item.notify_changes ?? item.notifications
      )
    };
  }

  private mapPermissionRequest(item: PermissionRequestApiItem, fallbackId: number): PermissionRequest | null {
    const id = item.id || item.request_id || fallbackId;
    if (!id) {
      return null;
    }

    return {
      id,
      userName:
        item.full_name ||
        item.user_name ||
        `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
        item.username ||
        `User #${item.user_id || item.userId || id}`,
      requestedAt: item.requested_at || item.created_at || ''
    };
  }

  private normalizeShareRole(value: string | undefined): ShareRole {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'owner') {
      return 'owner';
    }

    if (normalized === 'writer' || normalized === 'editor' || normalized === 'write') {
      return 'writer';
    }

    return 'reader';
  }

  private normalizeMembershipStatus(value: string | undefined): MembershipStatus {
    const normalized = (value || '').toLowerCase();

    if (normalized === 'pending' || normalized === 'requested') {
      return 'pending';
    }

    if (normalized === 'member' || normalized === 'joined' || normalized === 'accepted') {
      return 'member';
    }

    if (normalized === 'owner') {
      return 'owner';
    }

    return 'none';
  }

  private navigateTo(path: string, replace = false): void {
    const current = `${window.location.pathname}${window.location.search}`;
    if (current === path) {
      this.syncRouteFromUrl(path);
      return;
    }

    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    this.syncRouteFromUrl(path);
  }

  private syncRouteFromUrl(url: string): void {
    const pathname = (url || '').split('?')[0];
    const listMatch = pathname.match(/^\/lists\/(\d+)$/);

    if (pathname === '/about') {
      this.currentPage = 'about';
      return;
    }

    if (listMatch) {
      const routeListId = Number(listMatch[1]);
      this.currentPage = 'list';
      if (Number.isFinite(routeListId) && routeListId > 0 && this.activeListId !== routeListId) {
        this.activeListId = routeListId;
        this.persistActiveListId(routeListId);
        this.loadTodos();
      }
      return;
    }

    this.currentPage = 'main';
  }

  private refreshLocationDebugData(): void {
    this.debugLocationHref = window.location.href;
    this.debugLocationSearch = window.location.search;
    this.debugLocationHash = window.location.hash;
  }

  private loadBuildVersion(): void {
    this.http.get<BuildVersionResponse>('/version.json').subscribe({
      next: (response) => {
        this.buildVersion = response.version || 'unknown';
        this.cdr.markForCheck();
      },
      error: () => {
        this.buildVersion = 'unknown';
        this.cdr.markForCheck();
      }
    });
  }

  private loadBackendVersion(): void {
    this.http
      .get<BuildVersionResponse | string>(`${environment.apiBaseUrl}/version`, this.getAuthHttpOptions())
      .subscribe({
        next: (response) => {
          this.backendVersion =
            typeof response === 'string'
              ? response
              : response.version || 'unknown';
          this.cdr.markForCheck();
        },
        error: () => {
          this.backendVersion = 'unknown';
          this.cdr.markForCheck();
        }
      });
  }

  private initializeTelegramWebApp(): void {
    const validationIssues: string[] = [];
    const currentPath = window.location.pathname;
    const expectedPath = environment.telegramWebAppPath;
    const isLocalRuntime = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isHttpsOrLocalhost =
      window.location.protocol === 'https:' ||
      isLocalRuntime;

    if (!isHttpsOrLocalhost) {
      validationIssues.push('WebApp URL must use HTTPS (localhost is only for local tests).');
    }

    if (!window.isSecureContext) {
      validationIssues.push('Browser context is not secure. Telegram WebApp requires a secure context.');
    }

    if (!currentPath.startsWith(expectedPath)) {
      validationIssues.push(`Current path "${currentPath}" does not match configured Telegram path "${expectedPath}".`);
    }

    const hasStoredSession = this.restoreStoredAuthSession();
    const webApp = window.Telegram?.WebApp;
    if (!webApp) {
      this.telegramInitDataUnsafeDebug = '';
      validationIssues.push('Telegram JS SDK is missing. Open this page from Telegram or include its SDK script.');
      this.telegramStatusMessage = 'Running outside Telegram WebApp.';
      this.telegramValidationIssues = validationIssues;
      if (hasStoredSession) {
        this.telegramVerifyState = 'verified';
        this.telegramVerifyMessage = 'Using saved session.';
        this.loadTodoLists();
      } else if (isLocalRuntime) {
        this.verifyTelegramInitData();
      } else {
        this.loadTodoLists();
      }
      return;
    }

    this.telegramWebApp = webApp;
    this.isTelegramWebApp = true;
    this.telegramPlatform = webApp.platform || 'unknown';
    this.telegramVersion = webApp.version || 'unknown';
    this.telegramInitDataUnsafeDebug = this.stringifyDebug(webApp.initDataUnsafe || {});
    this.hasTelegramInitData = Boolean(webApp.initData);
    this.telegramInitDataRaw = webApp.initData || '';
    this.telegramInitDataEntries = this.parseTelegramInitData(webApp.initData || '');
    this.applyProfileFromInitData(webApp.initData || '');

    if (!this.hasTelegramInitData) {
      validationIssues.push('initData is empty. Open this URL using Telegram bot WebApp button.');
      if (hasStoredSession) {
        this.telegramVerifyState = 'verified';
        this.telegramVerifyMessage = 'Using saved session after Telegram reload.';
        this.loadTodoLists();
      } else if (isLocalRuntime) {
        this.verifyTelegramInitData();
      } else {
        this.telegramVerifyState = 'failed';
        this.telegramVerifyMessage = 'Launch data missing and there is no saved session.';
        this.finishStartupLoading();
      }
    } else {
      this.verifyTelegramInitData(webApp.initData, hasStoredSession);
    }

    webApp.ready();
    webApp.expand();
    webApp.disableVerticalSwipes?.();

    this.syncThemeFromTelegram();
    this.syncViewportFromTelegram();

    if (webApp.setHeaderColor) {
      webApp.setHeaderColor('secondary_bg_color');
    }

    webApp.onEvent('themeChanged', this.onThemeChanged);
    webApp.onEvent('viewportChanged', this.onViewportChanged);

    this.telegramStatusMessage = this.hasTelegramInitData
      ? 'Telegram WebApp connected.'
      : 'Telegram WebApp detected, but launch data is missing.';

    this.telegramValidationIssues = validationIssues;
  }

  private verifyTelegramInitData(initData?: string, allowStoredSessionFallback = false): void {
    this.telegramVerifyState = 'verifying';
    this.telegramVerifyMessage = 'Verifying launch data...';
    this.telegramVerifyErrorDebug = '';
    const payload = initData ? { initData } : {};

    this.http
      .post<TelegramVerifyResponse>(`${environment.apiBaseUrl}${environment.telegramVerifyEndpoint}`, payload)
      .subscribe({
        next: (response) => {
          this.telegramVerifyResponseDebug = this.stringifyDebug(this.maskTelegramVerifyResponse(response));
          const isRejected = response.ok !== true;
          const hasToken = Boolean(response.access_token);

          if (isRejected || !hasToken) {
            if (allowStoredSessionFallback && this.accessToken) {
              this.telegramVerifyState = 'verified';
              this.telegramVerifyMessage = 'Using saved session because launch data verification failed.';
              this.telegramValidationIssues = [...this.telegramValidationIssues, 'Telegram launch data verification failed.'];
              this.loadTodoLists();
            } else {
              this.clearStoredAuthSession();
              this.telegramVerifyState = 'failed';
              this.telegramVerifyMessage = isRejected
                ? 'Verification rejected by backend.'
                : 'Verification response has no access_token.';
              this.telegramValidationIssues = [...this.telegramValidationIssues, 'Telegram launch data verification failed.'];
              this.finishStartupLoading();
            }
          } else {
            this.accessToken = response.access_token || '';
            this.accessTokenType = response.token_type || 'Bearer';
            this.persistAuthSession(response);
            this.telegramVerifyState = 'verified';
            this.telegramVerifyMessage = 'Launch data verified by backend.';
            this.applyProfileFromVerifiedUser(response.user);
            this.loadTodoLists();
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          this.telegramVerifyErrorDebug = this.stringifyDebug({
            status: error?.status,
            message: error?.message,
            error: error?.error
          });
          if (allowStoredSessionFallback && this.accessToken) {
            this.telegramVerifyState = 'verified';
            this.telegramVerifyMessage = 'Using saved session because launch data verification request failed.';
            this.telegramValidationIssues = [
              ...this.telegramValidationIssues,
              `Cannot verify launch data via ${environment.telegramVerifyEndpoint}.`
            ];
            this.loadTodoLists();
          } else {
            this.clearStoredAuthSession();
            this.telegramVerifyState = 'failed';
            this.telegramVerifyMessage = `Verification request failed at ${environment.telegramVerifyEndpoint}.`;
            this.telegramValidationIssues = [
              ...this.telegramValidationIssues,
              `Cannot verify launch data via ${environment.telegramVerifyEndpoint}.`
            ];
            this.finishStartupLoading();
          }
          this.cdr.markForCheck();
        }
      });
  }

  private maskTelegramVerifyResponse(response: TelegramVerifyResponse): Record<string, unknown> {
    return {
      ...response,
      access_token: response.access_token ? this.maskToken(response.access_token) : '',
      user: response.user
        ? {
            ...response.user
          }
        : undefined
    };
  }

  private maskToken(token: string): string {
    if (!token) {
      return '';
    }

    if (token.length <= 10) {
      return '***';
    }

    return `${token.slice(0, 6)}...${token.slice(-4)}`;
  }

  private stringifyDebug(value: unknown): string {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  private getAuthHttpOptions(): { headers?: HttpHeaders } {
    if (!this.accessToken) {
      return {};
    }

    return {
      headers: new HttpHeaders({
        Authorization: `${this.accessTokenType} ${this.accessToken}`
      })
    };
  }

  private restoreStoredAuthSession(): boolean {
    const stored = this.readStoredAuthSession();
    if (!stored) {
      return false;
    }

    if (stored.expiresAt && stored.expiresAt <= Date.now()) {
      this.clearStoredAuthSession();
      return false;
    }

    this.accessToken = stored.accessToken;
    this.accessTokenType = stored.tokenType || 'Bearer';
    this.applyProfileFromVerifiedUser(stored.user);
    return Boolean(this.accessToken);
  }

  private persistAuthSession(response: TelegramVerifyResponse): void {
    if (!response.access_token) {
      return;
    }

    const expiresAt =
      typeof response.expires_in === 'number' && response.expires_in > 0
        ? Date.now() + response.expires_in * 1000
        : undefined;

    const payload: StoredAuthSession = {
      accessToken: response.access_token,
      tokenType: response.token_type || 'Bearer',
      expiresAt,
      user: response.user
    };

    this.writeStoredAuthSession(payload);
  }

  private readStoredAuthSession(): StoredAuthSession | null {
    try {
      const raw = window.localStorage.getItem(AppComponent.AUTH_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as StoredAuthSession;
      if (!parsed?.accessToken) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  private writeStoredAuthSession(payload: StoredAuthSession): void {
    try {
      window.localStorage.setItem(AppComponent.AUTH_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore storage write issues.
    }
  }

  private clearStoredAuthSession(): void {
    this.accessToken = '';
    this.accessTokenType = 'Bearer';
    try {
      window.localStorage.removeItem(AppComponent.AUTH_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup issues.
    }
  }

  private restoreActiveListId(): void {
    try {
      const raw = window.localStorage.getItem(AppComponent.ACTIVE_LIST_STORAGE_KEY);
      if (!raw) {
        return;
      }

      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        this.clearStoredActiveListId();
        return;
      }

      this.activeListId = parsed;
    } catch {
      // Ignore storage read issues.
    }
  }

  private persistActiveListId(listId: number | null): void {
    if (listId === null) {
      this.clearStoredActiveListId();
      return;
    }

    try {
      window.localStorage.setItem(AppComponent.ACTIVE_LIST_STORAGE_KEY, String(listId));
    } catch {
      // Ignore storage write issues.
    }
  }

  private clearStoredActiveListId(): void {
    try {
      window.localStorage.removeItem(AppComponent.ACTIVE_LIST_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup issues.
    }
  }

  private finishStartupLoading(): void {
    if (!this.isStartupLoading) {
      return;
    }

    this.isStartupLoading = false;
  }

  private parseTelegramInitData(initData: string): TelegramInitDataEntry[] {
    if (!initData) {
      return [];
    }

    const params = new URLSearchParams(initData);
    const entries: TelegramInitDataEntry[] = [];

    params.forEach((value, key) => {
      let parsedValue = value;
      if (key === 'user') {
        try {
          parsedValue = JSON.stringify(JSON.parse(value), null, 2);
        } catch {
          parsedValue = value;
        }
      }
      entries.push({ key, value: parsedValue });
    });

    return entries;
  }

  private applyProfileFromVerifiedUser(user: TelegramVerifiedUser | undefined): void {
    if (!user) {
      return;
    }

    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    this.profileName = fullName || user.username || this.profileName;
    this.profilePhotoUrl = user.photo_url || this.profilePhotoUrl;
  }

  private applyProfileFromInitData(initData: string): void {
    if (!initData) {
      return;
    }

    const rawUser = new URLSearchParams(initData).get('user');
    if (!rawUser) {
      return;
    }

    try {
      const parsed = JSON.parse(rawUser) as TelegramInitDataUser;
      const fullName = `${parsed.first_name || ''} ${parsed.last_name || ''}`.trim();
      this.profileName = fullName || parsed.username || this.profileName;
      this.profilePhotoUrl = parsed.photo_url || this.profilePhotoUrl;
    } catch {
      // Ignore invalid user payload.
    }
  }

  private syncThemeFromTelegram(): void {
    if (!this.telegramWebApp) {
      return;
    }

    const root = document.documentElement;
    const theme = this.telegramWebApp.themeParams;

    this.setCssVar(root, '--tg-bg-color', theme.bg_color);
    this.setCssVar(root, '--tg-text-color', theme.text_color);
    this.setCssVar(root, '--tg-hint-color', theme.hint_color);
    this.setCssVar(root, '--tg-link-color', theme.link_color);
    this.setCssVar(root, '--tg-button-color', theme.button_color);
    this.setCssVar(root, '--tg-button-text-color', theme.button_text_color);
    this.setCssVar(root, '--tg-secondary-bg-color', theme.secondary_bg_color);
  }

  private syncViewportFromTelegram(): void {
    if (!this.telegramWebApp) {
      return;
    }

    const root = document.documentElement;
    const stableHeight = this.telegramWebApp.viewportStableHeight;
    const height = this.telegramWebApp.viewportHeight;

    if (stableHeight) {
      root.style.setProperty('--tg-stable-viewport-height', `${stableHeight}px`);
    }

    if (height) {
      root.style.setProperty('--tg-viewport-height', `${height}px`);
    }
  }

  private setCssVar(root: HTMLElement, key: string, value: string | undefined): void {
    if (value) {
      root.style.setProperty(key, value);
    }
  }
}
